import React from "react";
import { EventBus } from "@engine/EventBus.js";
import { WorldStore } from "@store/WorldStore.js";
import { KeyringStore } from "@store/KeyringStore.js";
import { PartyStore } from "@store/PartyStore.js";
import {
  attemptLockpick,
  THIEVES_TOOLS_ITEM_ID,
} from "@engine/inventory/LockpickAction.js";
import { attemptForceOpen } from "@engine/inventory/ForceOpenAction.js";
import { abilityModifier } from "@engine/types/entity.js";
import type { PlayerCharacter } from "@engine/types/entity.js";

/**
 * I16 + I17 — lock menu.
 *
 * Opened via `ui:lock_menu_open` when the player clicks a locked container
 * or a locked door. Offers three actions:
 *
 * - **Use Key**: enabled only when the party's keyring contains an item
 *   matching the lock's `keyItemId`. Marks the target unlocked and closes.
 * - **Pick Lock (Thieves' Tools)**: enabled when the keyring contains
 *   `thieves-tools`. Picks the highest-DEX party member, rolls
 *   `d20 + DEX + prof` vs `lockDc`; success unlocks and keeps the tools,
 *   failure breaks the tools. (I17)
 * - **Force Open**: picks the highest-STR party member who hasn't yet
 *   tried this target, rolls `d20 + STR` vs `lockDc + 5`; success unlocks.
 *   Each PC gets one attempt per target. (I17)
 * - **Cancel**: close without changes.
 */
interface LockMenuState {
  targetKind: "container" | "door";
  targetId: string;
  lockDc: number;
  keyItemId?: string;
}

function bestByAbility(
  members: readonly PlayerCharacter[],
  ability: "DEX" | "STR",
  excludeIds: ReadonlySet<string> = new Set(),
): PlayerCharacter | null {
  let best: PlayerCharacter | null = null;
  let bestMod = -Infinity;
  for (const m of members) {
    if (excludeIds.has(m.instanceId)) continue;
    const mod = abilityModifier(m.abilities[ability]);
    if (mod > bestMod) {
      best = m;
      bestMod = mod;
    }
  }
  return best;
}

export function LockMenu() {
  const [state, setState] = React.useState<LockMenuState | null>(null);
  // Bump to force a re-read of KeyringStore / WorldStore after an action.
  const [, setTick] = React.useState(0);
  const refresh = () => setTick((n) => n + 1);

  React.useEffect(() => {
    const off = EventBus.on("ui:lock_menu_open", (evt) => {
      setState({
        targetKind: evt.targetKind,
        targetId: evt.targetId,
        lockDc: evt.lockDc,
        ...(evt.keyItemId !== undefined && { keyItemId: evt.keyItemId }),
      });
    });
    return off;
  }, []);

  if (state === null) return null;

  const { targetKind, targetId, lockDc, keyItemId } = state;
  const close = () => setState(null);
  const hasMatchingKey = keyItemId !== undefined && KeyringStore.has(keyItemId);
  const hasTools = KeyringStore.has(THIEVES_TOOLS_ITEM_ID);
  const members = PartyStore.getMembers();

  const markUnlocked = () => {
    if (targetKind === "container") {
      WorldStore.markContainerUnlocked(targetId);
    } else {
      WorldStore.markDoorUnlocked(targetId);
    }
  };

  const useKey = () => {
    if (!hasMatchingKey) return;
    markUnlocked();
    EventBus.emit("ui:combat_log_push", {
      message: "You unlock it with the key.",
    });
    close();
  };

  const pickLock = () => {
    if (!hasTools) return;
    const picker = bestByAbility(members, "DEX");
    if (!picker) {
      EventBus.emit("ui:combat_log_push", {
        message: "No one is available to pick the lock.",
      });
      return;
    }
    const result = attemptLockpick({ picker, lockDc, hasTools });
    if (result.outcome === "no_tools") return;
    if (result.toolsBroken) {
      KeyringStore.remove(THIEVES_TOOLS_ITEM_ID);
    }
    if (result.outcome === "success") {
      markUnlocked();
      EventBus.emit("ui:combat_log_push", {
        message: `${picker.name} picks the lock (rolled ${result.total} vs DC ${lockDc}).`,
      });
      close();
    } else {
      EventBus.emit("ui:combat_log_push", {
        message: `${picker.name} fumbles the pick — tools snap (rolled ${result.total} vs DC ${lockDc}).`,
      });
      refresh();
    }
  };

  const attemptedIds = new Set(WorldStore.getForceOpenAttempts(targetKind, targetId));
  const forcer = bestByAbility(members, "STR", attemptedIds);
  const allAttempted = members.length > 0 && forcer === null;

  const forceOpen = () => {
    if (!forcer) return;
    const result = attemptForceOpen({
      actor: forcer,
      lockDc,
      alreadyAttempted: false,
    });
    if (result.outcome === "already_attempted") return;
    WorldStore.recordForceOpenAttempt(targetKind, targetId, forcer.instanceId);
    if (result.outcome === "success") {
      markUnlocked();
      EventBus.emit("ui:combat_log_push", {
        message: `${forcer.name} forces it open (rolled ${result.total} vs DC ${result.dc}).`,
      });
      close();
    } else {
      EventBus.emit("ui:combat_log_push", {
        message: `${forcer.name} throws their weight at it — it doesn't budge (rolled ${result.total} vs DC ${result.dc}).`,
      });
      refresh();
    }
  };

  const title = targetKind === "container" ? "Locked Container" : "Locked Door";

  const buttonStyle = (enabled: boolean, accent = false) => ({
    background: enabled ? (accent ? "#2a3a5a" : "#222") : "#1a1f2a",
    color: enabled ? (accent ? "#cce" : "#aab") : "#556",
    border: `1px solid ${enabled ? (accent ? "#557" : "#445") : "#334"}`,
    padding: "8px 14px",
    cursor: enabled ? "pointer" : "not-allowed",
    fontFamily: "monospace",
    fontSize: 12,
    borderRadius: 2,
    textAlign: "left" as const,
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        zIndex: 200,
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 360,
          maxWidth: "92vw",
          background: "#101420",
          color: "#e8e8e8",
          border: "1px solid #445",
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.7)",
          padding: "18px 22px",
          fontFamily: "monospace",
        }}
      >
        <div
          style={{
            fontSize: 14,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "#aab",
            borderBottom: "1px solid #334",
            paddingBottom: 10,
            marginBottom: 14,
          }}
        >
          {title}
        </div>

        <div style={{ fontSize: 13, color: "#cce", marginBottom: 16 }}>
          {keyItemId !== undefined
            ? hasMatchingKey
              ? `You have a matching key (${keyItemId}).`
              : `Requires key: ${keyItemId}.`
            : "No key will fit this lock."}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <button
            onClick={useKey}
            disabled={!hasMatchingKey}
            style={buttonStyle(hasMatchingKey, true)}
          >
            Use Key
          </button>
          <button
            onClick={pickLock}
            disabled={!hasTools}
            title={hasTools ? undefined : "Requires Thieves' Tools"}
            style={buttonStyle(hasTools)}
          >
            Pick Lock (Thieves&apos; Tools)
          </button>
          <button
            onClick={forceOpen}
            disabled={!forcer}
            title={
              allAttempted
                ? "Every party member has already tried"
                : !forcer
                  ? "No one is available"
                  : undefined
            }
            style={buttonStyle(!!forcer)}
          >
            Force Open{forcer ? ` (${forcer.name})` : ""}
          </button>
          <button onClick={close} style={buttonStyle(true)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
